import React, { useMemo, useState } from "react";
import {
  Typography,
  Grid,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { Link } from "react-router-dom";

import "./TeamDraftProspectsSummary.css";

const COLUMNS = {
  player: "player",
  draft_year: "draft_year",
  overall_pick: "overall_pick",
  round_pick: "round_pick",
  years_post_draft: "years_post_draft",
};

function yearsLabel(n) {
  if (n == null || Number.isNaN(n)) {
    return "—";
  }
  return `${n} year${n === 1 ? "" : "s"} since draft`;
}

function playerSortKey(p) {
  return `${p.last_name || ""} ${p.first_name || ""}`.trim();
}

function compareRows(a, b, orderBy, order) {
  const dir = order === "asc" ? 1 : -1;
  let cmp = 0;

  switch (orderBy) {
    case COLUMNS.player:
      cmp = playerSortKey(a).localeCompare(playerSortKey(b), undefined, {
        sensitivity: "base",
      });
      break;
    case COLUMNS.draft_year:
      cmp = (a.draft_year ?? 0) - (b.draft_year ?? 0);
      break;
    case COLUMNS.overall_pick:
      cmp = (a.overall_pick ?? 0) - (b.overall_pick ?? 0);
      break;
    case COLUMNS.round_pick: {
      const r = (a.round ?? 0) - (b.round ?? 0);
      cmp = r !== 0 ? r : (a.pick ?? 0) - (b.pick ?? 0);
      break;
    }
    case COLUMNS.years_post_draft:
      cmp = (a.years_post_draft ?? 0) - (b.years_post_draft ?? 0);
      break;
    default:
      cmp = 0;
  }

  if (cmp !== 0) {
    return cmp * dir;
  }

  const y = (b.draft_year ?? 0) - (a.draft_year ?? 0);
  if (y !== 0) {
    return y;
  }
  return (a.overall_pick ?? 0) - (b.overall_pick ?? 0);
}

/** Rows shown before “Show all”; full list remains sortable when expanded. */
const INITIAL_VISIBLE_ROWS = 8;

function SortableProspectsTable({ prospects }) {
  const [orderBy, setOrderBy] = useState(COLUMNS.draft_year);
  const [order, setOrder] = useState("desc");
  const [showAll, setShowAll] = useState(false);

  const sorted = useMemo(
    () => [...prospects].sort((a, b) => compareRows(a, b, orderBy, order)),
    [prospects, orderBy, order]
  );

  const visible = showAll
    ? sorted
    : sorted.slice(0, INITIAL_VISIBLE_ROWS);
  const hasMore = sorted.length > INITIAL_VISIBLE_ROWS;

  const handleRequestSort = (property) => {
    if (orderBy === property) {
      setOrder((o) => (o === "asc" ? "desc" : "asc"));
    } else {
      setOrderBy(property);
      const defaultDesc =
        property === COLUMNS.draft_year ||
        property === COLUMNS.years_post_draft;
      setOrder(defaultDesc ? "desc" : "asc");
    }
  };

  const cellSx = {
    borderColor: "var(--border-subtle)",
    color: "var(--text-primary)",
    fontSize: "0.82rem",
    py: 1.25,
    px: 1.75,
  };

  const headSx = {
    ...cellSx,
    fontSize: "0.68rem",
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: "var(--text-muted)",
    whiteSpace: "nowrap",
    borderBottom: "1px solid var(--border-subtle)",
  };

  const sortLabelSx = {
    color: "inherit",
    "&.Mui-active": { color: "var(--text-primary)" },
    "& .MuiTableSortLabel-icon": { color: "var(--text-muted) !important" },
  };

  return (
    <TableContainer className="team-draft-summary-table-wrap">
      <Table
        className="team-draft-summary-mui-table"
        size="small"
        aria-label="Drafted prospects, sortable"
      >
        <TableHead>
          <TableRow>
            <TableCell sx={headSx}>
              <TableSortLabel
                active={orderBy === COLUMNS.player}
                direction={orderBy === COLUMNS.player ? order : "asc"}
                onClick={() => handleRequestSort(COLUMNS.player)}
                sx={sortLabelSx}
              >
                Player
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headSx}>
              <TableSortLabel
                active={orderBy === COLUMNS.draft_year}
                direction={orderBy === COLUMNS.draft_year ? order : "asc"}
                onClick={() => handleRequestSort(COLUMNS.draft_year)}
                sx={sortLabelSx}
              >
                Class
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headSx} align="right">
              <TableSortLabel
                active={orderBy === COLUMNS.overall_pick}
                direction={orderBy === COLUMNS.overall_pick ? order : "asc"}
                onClick={() => handleRequestSort(COLUMNS.overall_pick)}
                sx={sortLabelSx}
              >
                Overall
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headSx} align="right">
              <TableSortLabel
                active={orderBy === COLUMNS.round_pick}
                direction={orderBy === COLUMNS.round_pick ? order : "asc"}
                onClick={() => handleRequestSort(COLUMNS.round_pick)}
                sx={sortLabelSx}
              >
                Rnd · Pick
              </TableSortLabel>
            </TableCell>
            <TableCell sx={headSx} align="right">
              <TableSortLabel
                active={orderBy === COLUMNS.years_post_draft}
                direction={
                  orderBy === COLUMNS.years_post_draft ? order : "asc"
                }
                onClick={() => handleRequestSort(COLUMNS.years_post_draft)}
                sx={sortLabelSx}
              >
                Post-draft
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {visible.map((p) => (
            <TableRow
              key={`${p.player_id}-${p.draft_year}-${p.round}-${p.pick}`}
              hover
              sx={{ "&:last-child td": { borderBottom: 0 } }}
            >
              <TableCell sx={cellSx}>
                <Link
                  to={`/player/${p.player_id}`}
                  className="team-draft-summary-player-link"
                >
                  {p.first_name} {p.last_name}
                </Link>
                {p.position && (
                  <span className="team-draft-summary-pos">{p.position}</span>
                )}
              </TableCell>
              <TableCell sx={{ ...cellSx, color: "var(--text-muted)" }}>
                {p.draft_year}
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  color: "var(--text-muted)",
                  fontVariantNumeric: "tabular-nums",
                }}
                align="right"
              >
                {p.overall_pick != null ? `${p.overall_pick}` : "—"}
              </TableCell>
              <TableCell
                sx={{
                  ...cellSx,
                  color: "var(--text-muted)",
                  fontVariantNumeric: "tabular-nums",
                }}
                align="right"
              >
                {p.round != null && p.pick != null
                  ? `${p.round} · ${p.pick}`
                  : "—"}
              </TableCell>
              <TableCell
                sx={{ ...cellSx, color: "var(--text-muted)" }}
                align="right"
              >
                {yearsLabel(p.years_post_draft)}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
      {hasMore && (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
            py: 1.25,
            px: 2,
            borderTop: "1px solid var(--border-subtle)",
          }}
        >
          {!showAll && (
            <Typography
              variant="caption"
              sx={{ color: "var(--text-muted)", letterSpacing: "0.02em" }}
            >
              Showing {visible.length} of {sorted.length}
            </Typography>
          )}
          <Button
            size="small"
            variant="text"
            onClick={() => setShowAll((v) => !v)}
            sx={{
              color: "var(--brand-strong)",
              fontWeight: 600,
              textTransform: "none",
              fontSize: "0.8rem",
            }}
          >
            {showAll
              ? `Show fewer (${INITIAL_VISIBLE_ROWS})`
              : `Show all ${sorted.length}`}
          </Button>
        </Box>
      )}
    </TableContainer>
  );
}

function TeamDraftProspectsSummary({ loading, summary }) {
  if (loading) {
    return (
      <Grid item xs={12}>
        <Box className="container team-draft-summary-section">
          <div className="team-draft-summary-header">
            <Typography variant="body1" className="team-draft-summary-title">
              Drafted prospects
            </Typography>
          </div>
          <Typography
            variant="body2"
            sx={{ px: 2, py: 2, color: "var(--text-muted)" }}
          >
            Loading summary…
          </Typography>
        </Box>
      </Grid>
    );
  }

  if (!loading && !summary) {
    return (
      <Grid item xs={12}>
        <Box className="container team-draft-summary-section">
          <div className="team-draft-summary-header">
            <Typography variant="body1" className="team-draft-summary-title">
              Drafted prospects
            </Typography>
          </div>
          <Typography
            variant="body2"
            sx={{ px: 2, py: 2, color: "var(--text-muted)" }}
          >
            Draft summary could not be loaded.
          </Typography>
        </Box>
      </Grid>
    );
  }

  if (!summary || !summary.prospects) {
    return null;
  }

  const { prospects, as_of_year, total } = summary;

  return (
    <Grid item xs={12}>
      <Box className="container team-draft-summary-section">
        <div className="team-draft-summary-header">
          <Typography variant="body1" className="team-draft-summary-title">
            Drafted prospects
          </Typography>
          {as_of_year != null && (
            <Typography variant="body2" className="team-draft-summary-meta">
              Years since draft as of {as_of_year}
            </Typography>
          )}
        </div>

        {total === 0 ? (
          <Typography
            variant="body2"
            sx={{ px: 2, py: 2, color: "var(--text-muted)" }}
          >
            No drafted prospects on file for this team yet.
          </Typography>
        ) : (
          <SortableProspectsTable
            key={summary.team_id}
            prospects={prospects}
          />
        )}
      </Box>
    </Grid>
  );
}

export default TeamDraftProspectsSummary;
